/**
 * The Closing prioritisation pop-up — the "panic" spike. Fires as a BLOCKING modal interrupt tied to
 * its host scenario (CLOSE-08, after the player has answered it); the player orders the competing tasks
 * by priority, then sees which order was right before returning to the deal.
 *
 * The initial task order is shuffled per run (lib/shuffle.ts) so the target ordering can't be memorised.
 * Two phases: (1) order the tasks — by DRAG or by the up/down buttons; (2) on confirm, reveal the model
 * priority order + how the player placed each task + the debrief, then continue (which submits the
 * ranking for scoring).
 * Accessibility: role="dialog" aria-modal; reordering is keyboard-first via up/down buttons (drag is an
 * additive affordance — native HTML5 drag is mouse-only, so the buttons remain the accessible path).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Check, GripVertical, Siren, X } from "lucide-react";
import type { PopupScenario } from "@patc/content-schema";
import { popupBandDistance, type PopupRankEntry } from "../game/scoring";
import { seededOrder } from "../lib/shuffle";
import { useFocusTrap } from "../lib/useFocusTrap";

export function PopupDialog({
  popup,
  seed,
  onSubmit,
}: {
  popup: PopupScenario;
  seed: number;
  onSubmit: (ranking: PopupRankEntry[]) => void;
}) {
  const [order, setOrder] = useState<string[]>(() =>
    seededOrder(
      popup.tasks.map((t) => t.id),
      seed,
      popup.id,
    ),
  );
  const [submitted, setSubmitted] = useState(false);
  const [announce, setAnnounce] = useState("");
  const dragIndex = useRef<number | null>(null);
  // The pop-up is a blocking interrupt with no dismiss path (you must triage), so no onEscape.
  const dialogRef = useFocusTrap<HTMLDivElement>();
  const taskById = new Map(popup.tasks.map((t) => [t.id, t]));

  function announceMove(taskId: string, toIndex: number) {
    setAnnounce(`${taskById.get(taskId)?.text ?? "Task"} moved to position ${toIndex + 1} of ${order.length}.`);
  }

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    announceMove(order[i]!, j);
    setOrder((o) => {
      const next = [...o];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }

  // ponytail: native HTML5 drag — mouse-only, no touch; up/down buttons are the accessible/touch path.
  function reorder(from: number, to: number) {
    if (from === to) return;
    announceMove(order[from]!, to);
    setOrder((o) => {
      const next = [...o];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 p-4 outline-none backdrop-blur-sm sm:items-center"
    >
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>
      <div className="flex max-h-[92svh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-ink-200 bg-card shadow-[var(--shadow-lg)]">
        <div className="flex items-center gap-2 bg-brand px-5 py-3 text-white motion-safe:animate-pulse">
          <Siren size={18} strokeWidth={2} aria-hidden="true" />
          <h2 id="popup-title" className="font-sans text-sm font-semibold uppercase tracking-[0.18em]">
            Everything at once
          </h2>
        </div>

        {!submitted ? (
          <>
            <div className="overflow-y-auto px-5 py-4">
              <p className="text-sm leading-relaxed text-ink-700">{popup.description}</p>
              <p className="mt-4 font-medium text-ink">{popup.prompt}</p>
              <p className="mt-1 text-xs text-ink-600">Drag to reorder, or use the arrows.</p>
              <ol className="mt-3 flex flex-col gap-2">
                {order.map((id, i) => {
                  const t = taskById.get(id)!;
                  return (
                    <li
                      key={id}
                      draggable
                      onDragStart={() => (dragIndex.current = i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragIndex.current !== null) reorder(dragIndex.current, i);
                        dragIndex.current = null;
                      }}
                      className="flex items-center gap-2 rounded-sm border border-ink-200 bg-panel px-3 py-2"
                    >
                      <GripVertical
                        size={16}
                        strokeWidth={2}
                        aria-hidden="true"
                        className="shrink-0 cursor-grab text-ink-400"
                      />
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-ink font-mono text-sm text-white"
                        aria-hidden="true"
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1 text-sm leading-snug text-ink-800">{t.text}</span>
                      <span className="flex shrink-0 flex-col">
                        <button
                          type="button"
                          onClick={() => move(i, -1)}
                          disabled={i === 0}
                          aria-label={`Move task ${i + 1} up`}
                          className="rounded-sm p-1.5 text-ink-500 hover:text-ink disabled:opacity-30"
                        >
                          <ArrowUp size={16} strokeWidth={2} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(i, 1)}
                          disabled={i === order.length - 1}
                          aria-label={`Move task ${i + 1} down`}
                          className="rounded-sm p-1.5 text-ink-500 hover:text-ink disabled:opacity-30"
                        >
                          <ArrowDown size={16} strokeWidth={2} aria-hidden="true" />
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-ink-150 px-5 py-3">
              <p className="text-xs text-ink-600">Order every task, then confirm.</p>
              <button
                type="button"
                onClick={() => setSubmitted(true)}
                className="inline-flex items-center gap-2 rounded-sm bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover"
              >
                Confirm priorities <span aria-hidden="true">→</span>
              </button>
            </div>
          </>
        ) : (
          <PopupReveal
            popup={popup}
            order={order}
            onContinue={() => onSubmit(order.map((taskId, i) => ({ taskId, rank: i + 1 })))}
          />
        )}
      </div>
    </div>
  );
}

/** After confirming: the model priority order, how the player placed each task, and the debrief. */
function PopupReveal({
  popup,
  order,
  onContinue,
}: {
  popup: PopupScenario;
  order: string[];
  onContinue: () => void;
}) {
  const placedRank = new Map(order.map((id, i) => [id, i + 1]));
  // Model order: tasks sorted by accepted band (low, then high). Tasks sharing a band are equally correct.
  const model = [...popup.tasks].sort((a, b) => a.correctBand[0] - b.correctBand[0] || a.correctBand[1] - b.correctBand[1]);
  const offByTotal = popupBandDistance(
    popup,
    order.map((taskId, i) => ({ taskId, rank: i + 1 })),
  );
  // Move focus to the reveal so a keyboard/SR user lands on the new content (the Confirm button unmounted).
  const revealRef = useRef<HTMLDivElement>(null);
  useEffect(() => revealRef.current?.focus(), []);

  return (
    <>
      <div ref={revealRef} tabIndex={-1} className="overflow-y-auto px-5 py-4 outline-none">
        <div className="flex items-center justify-between gap-2">
          <h3 className="t-overline text-ink-600">The priority that holds up</h3>
          <span className="text-xs font-medium text-ink-600">
            {offByTotal === 0 ? "Every task in band" : `Off by ${offByTotal} position${offByTotal === 1 ? "" : "s"}`}
          </span>
        </div>
        <ol className="mt-3 flex flex-col gap-2">
          {model.map((t) => {
            const [lo, hi] = t.correctBand;
            const placed = placedRank.get(t.id) ?? 0;
            const inBand = placed >= lo && placed <= hi;
            const bandLabel = lo === hi ? `${lo}` : `${lo}–${hi}`;
            return (
              <li key={t.id} className="flex items-start gap-3 rounded-sm border border-ink-150 bg-panel px-3 py-2">
                <span
                  className="mt-0.5 flex h-7 shrink-0 items-center justify-center rounded-sm bg-ink px-2 font-mono text-xs text-white"
                  aria-hidden="true"
                >
                  {bandLabel}
                </span>
                <span className="min-w-0 flex-1 text-sm leading-snug text-ink-800">{t.text}</span>
                <span className="shrink-0 text-xs font-medium">
                  {inBand ? (
                    <span className="inline-flex items-center gap-1 text-[var(--success)]">
                      <Check size={14} strokeWidth={2.5} aria-hidden="true" /> you: {placed}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[var(--danger)]">
                      <X size={14} strokeWidth={2.5} aria-hidden="true" /> you: {placed}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
        {popup.debrief && <p className="mt-4 text-sm leading-relaxed text-ink-600">{popup.debrief}</p>}
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-ink-150 px-5 py-3">
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex items-center gap-2 rounded-sm bg-ink px-4 py-2 text-sm font-medium text-white hover:bg-ink-800"
        >
          Continue to the deal <span aria-hidden="true">→</span>
        </button>
      </div>
    </>
  );
}
